import { SignalingMessageType, SignalingService } from "../services";
import { RoomStreamingBloc } from "../RoomStreamingBloc";
import {
  AnswerCommand,
  ConnectionCommand,
  DisconnectionCommand,
  IceCandidateCommand,
  OfferCommand
} from "./commands";
import { RefreshUserlistCommand } from "./commands/streaming/RefreshUserlistCommand";

export interface IStreamingCommand {
  execute(e: unknown): void;
}

export class StreamingHandler {
  private commands: Map<SignalingMessageType, IStreamingCommand> = new Map();
  private constructor(
    private bloc: RoomStreamingBloc,
    private signaling: SignalingService,
    private config: RTCConfiguration
  ) {
    this.commands.set(
      SignalingMessageType.CONNECT,
      new ConnectionCommand(bloc, signaling)
    );
    this.commands.set(
      SignalingMessageType.DISCONNECT,
      new DisconnectionCommand(bloc, signaling)
    );
    this.commands.set(
      SignalingMessageType.OFFER,
      new OfferCommand(bloc, signaling)
    );
    this.commands.set(
      SignalingMessageType.ANSWER,
      new AnswerCommand(bloc, signaling)
    );
    this.commands.set(
      SignalingMessageType.CANDIDATE,
      new IceCandidateCommand(bloc, signaling)
    );
    this.commands.set(
      SignalingMessageType.USERLIST,
      new RefreshUserlistCommand(bloc, signaling, config)
    );
  }

  public subscribe() {
    const connectionCommand = this.commands.get(SignalingMessageType.CONNECT);
    if (connectionCommand) {
      this.signaling.addEventListener(
        SignalingMessageType.CONNECT,
        connectionCommand.execute.bind(connectionCommand)
      );
    }
    const disconnectCommand = this.commands.get(
      SignalingMessageType.DISCONNECT
    );
    if (disconnectCommand) {
      this.signaling.addEventListener(
        SignalingMessageType.DISCONNECT,
        disconnectCommand.execute.bind(disconnectCommand)
      );
    }
    const offerCommand = this.commands.get(SignalingMessageType.OFFER);
    if (offerCommand) {
      this.signaling.addEventListener(
        SignalingMessageType.OFFER,
        offerCommand.execute.bind(offerCommand)
      );
    }
    const answerCommand = this.commands.get(SignalingMessageType.ANSWER);
    if (answerCommand) {
      this.signaling.addEventListener(
        SignalingMessageType.ANSWER,
        answerCommand.execute.bind(answerCommand)
      );
    }
    const candidateCommand = this.commands.get(SignalingMessageType.CANDIDATE);
    if (candidateCommand) {
      this.signaling.addEventListener(
        SignalingMessageType.CANDIDATE,
        candidateCommand.execute.bind(candidateCommand)
      );
    }
    const refreshUserlistCommand = this.commands.get(
      SignalingMessageType.USERLIST
    );
    if (refreshUserlistCommand) {
      this.signaling.addEventListener(
        SignalingMessageType.USERLIST,
        refreshUserlistCommand.execute.bind(refreshUserlistCommand)
      );
    }
  }

  public start() {
    const room = this.bloc.state.room;
    if (room === null) throw new Error("Room is null");
    if (room.localParticipant === null) return;
    const localParticipant = room.localParticipant;
    this.signaling.createConnection(
      room.id,
      localParticipant.displayName,
      localParticipant.type
    );
  }

  public static create(
    bloc: RoomStreamingBloc,
    signaling: SignalingService,
    config: RTCConfiguration
  ): StreamingHandler {
    const handler = new StreamingHandler(bloc, signaling, config);
    handler.subscribe();
    handler.start();
    return handler;
  }
}
